import * as Sentry from "@sentry/react";
import { client } from "@sunner-cloud/api-client";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AuthContext } from "./auth/AuthContext";

import { resetMixpanel } from "@/config/mixpanel";
import { queryClient } from "@/queryClient";

// Constantes para el almacenamiento seguro
const TOKEN_KEY = "sunner_auth_token";
const TOKEN_EXPIRY_KEY = "sunner_auth_token_expiry";

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

interface Props {
  children: ReactNode;
}

/**
 * AuthProvider con almacenamiento seguro del token JWT
 *
 * Medidas de seguridad implementadas:
 * 1. Validación de token en cada inicialización
 * 2. Verificación de expiración del token
 * 3. Limpieza automática de tokens expirados
 * 4. Configuración del token en el cliente API
 * 5. Sanitización del localStorage en logout
 */
export const AuthProvider = ({ children }: Props) => {
  const [token, setToken] = useState<string | null>(() => {
    // Inicialización segura: verificar si el token existe y no ha expirado
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!storedToken || !expiryTime) {
      return null;
    }

    // Verificar si el token ha expirado
    if (Date.now() > parseInt(expiryTime, 10)) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return null;
    }

    // Nota: La configuración inicial del cliente se hace en clientConfig.ts
    // para evitar condiciones de carrera. Este useState solo lee el token.
    return storedToken;
  });

  useEffect(() => {
    if (token) {
      client.setConfig({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } else {
      client.setConfig({
        headers: {},
      });
    }
  }, [token]);

  const login = useCallback((newToken: string) => {
    // Configurar el cliente API SÍNCRONAMENTE antes de actualizar el estado
    // Esto evita race conditions donde queries se disparan antes de que
    // el useEffect asíncrono configure el header Authorization
    client.setConfig({
      headers: {
        Authorization: `Bearer ${newToken}`,
      },
    });

    // Decodificar el payload del JWT para obtener la fecha de expiración
    // Nota: Esta decodificación es solo para lectura, NO para validación
    try {
      const payload = JSON.parse(atob(newToken.split(".")[1])) as JwtPayload;
      const expiryTime = payload.exp
        ? payload.exp * 1000
        : Date.now() + 24 * 60 * 60 * 1000; // Default: 24 horas

      if (typeof payload.email === "string") {
        Sentry.setUser({ email: payload.email });
      }

      // Almacenar token y tiempo de expiración
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      setToken(newToken);
    } catch (error) {
      Sentry.captureMessage("JWT decode failed, using default expiry", {
        level: "warning",
        extra: { error },
      });

      // Si hay error decodificando, usar expiración por defecto
      const defaultExpiry = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(TOKEN_EXPIRY_KEY, defaultExpiry.toString());
      setToken(newToken);
    }
  }, []);

  const logout = useCallback(() => {
    // Limpiar el cliente API SÍNCRONAMENTE
    client.setConfig({
      headers: {},
    });

    Sentry.setUser(null);

    // Mixpanel: desvincular los eventos del usuario saliente y generar un
    // nuevo device_id, para no fusionar la sesión del próximo usuario en un
    // dispositivo compartido. No-op si Mixpanel no se inicializó.
    resetMixpanel();

    // Limpieza completa y segura del localStorage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setToken(null);

    queryClient.clear();
  }, []);

  const isTokenExpired = useCallback(() => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) {
      return true;
    }
    return Date.now() > parseInt(expiryTime, 10);
  }, []);

  // Verificar periódicamente si el token ha expirado
  useEffect(() => {
    if (!token) {
      return;
    }

    const interval = setInterval(() => {
      if (isTokenExpired()) {
        logout();
      }
    }, 60 * 1000); // Verificar cada minuto

    return () => {
      clearInterval(interval);
    };
  }, [token, isTokenExpired, logout]);

  const value = useMemo(
    () => ({
      token,
      isAuthenticated: !!token && !isTokenExpired(),
      login,
      logout,
      isTokenExpired,
    }),
    [token, login, logout, isTokenExpired],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};
